import { readableDate } from '@chocolate-soup-inc/cs-api-consumer-utils';
import { Card, Chip, Icon, Tooltip } from '@chocolate-soup-inc/cs-frontend-components';
import clsx from 'clsx';
import { useMemo } from 'react';
import { TDraggableCardRenderOptions } from '../../components/board/DraggableCard';
import { getReadableWorkAnniversaryVariation, getWorkAnniversaryIcon } from '../../entities/companies/shared';
import { getReadableRelationshipType } from '../../entities/dependants/shared';
import { getReadableGiftType, getRemovedInfoFromRecipientData, isDonationGift } from '../../entities/gifts/shared';
import { isDependant } from '../../entities/recipients/shared';

import { AlertsTooltip } from '../../components/alerts/AlertsTooltip';
import { getReadableDeliveryMethodType, isAddressValid } from '../../entities/addresses/shared';
import { TGiftType } from '../../entities/gifts/queries';
import { TAlert, TDeliveryMethod, TGiftTypes, TWorkAnniversaryGiftVariations } from '../../generated/graphql';
import { BRICK_COLOR_CATALOG } from '../../entities/events/oneOff';
import styles from './GiftCard.module.scss';

export type TGiftCardProps = {
  filteredGifts?: TGiftType[];
  gift: TGiftType;
  options?: TDraggableCardRenderOptions & {
    hoverDisabled?: boolean;
  };
};

export const GiftCard = (props: TGiftCardProps) => {
  const { filteredGifts, gift, options: { hoverDisabled = false, isSelected, isDragging, measureElement, overlay, rowIndexAttribute, rowIndexAttributeName } = {} } = props;

  const totalGroupItems = useMemo(() => {
    return gift.groupedGifts?.length || 1;
  }, [gift]);

  const totalColumnGroupItems = useMemo(() => {
    return gift.groupedGifts?.filter((g) => g.status === gift.status).length || 1;
  }, [gift]);

  const groupedColumnGifts = useMemo(() => {
    return gift.groupedGifts?.filter((g) => g.status === gift.status);
  }, [gift]);

  const filteredGroupedColumnGifts = useMemo(() => {
    return groupedColumnGifts?.filter((g) => filteredGifts?.find((g1) => g1.id === g.id) != null);
  }, [filteredGifts, groupedColumnGifts]);

  const groupColumnPosition = useMemo(() => {
    if (groupedColumnGifts) return groupedColumnGifts.findIndex((g) => g.id === gift.id);
    else return 0;
  }, [gift.id, groupedColumnGifts]);

  const isGroupFirst = useMemo(() => {
    if (gift.groupedGifts == null || gift.groupedGifts.length === 0) return false;

    if (filteredGifts && filteredGroupedColumnGifts) {
      const index = filteredGroupedColumnGifts.findIndex((g) => g.id === gift.id);

      return index === 0;
    } else return groupColumnPosition === 0;
  }, [filteredGifts, filteredGroupedColumnGifts, gift.groupedGifts, gift.id, groupColumnPosition]);

  const isGroupLast = useMemo(() => {
    if (gift.groupedGifts == null || gift.groupedGifts.length === 0) return false;

    if (filteredGifts && filteredGroupedColumnGifts) {
      const index = filteredGroupedColumnGifts.findIndex((g) => g.id === gift.id);
      return index === filteredGroupedColumnGifts.length - 1;
    } else return groupColumnPosition + 1 === totalColumnGroupItems;
  }, [filteredGifts, filteredGroupedColumnGifts, gift.groupedGifts, gift.id, groupColumnPosition, totalColumnGroupItems]);

  const groupFilteredAmount = useMemo(() => {
    if (filteredGroupedColumnGifts && groupedColumnGifts) return groupedColumnGifts.length - filteredGroupedColumnGifts.length;
    else return 0;
  }, [filteredGroupedColumnGifts, groupedColumnGifts]);

  const isFiltered = useMemo(() => {
    return groupFilteredAmount !== 0;
  }, [groupFilteredAmount]);

  const isDonation = useMemo(() => {
    return isDonationGift({
      gift
    });
  }, [gift]);

  const missingAddress = useMemo(() => {
    return gift.addressId == null || !isAddressValid(gift.address);
  }, [gift.addressId, gift.address]);

  const employeeIsDeleted = useMemo(() => {
    return gift.addressId?.includes(import.meta.env.REACT_APP_EMPLOYEES_TABLE_NAME as string) && !gift.employee;
  }, [gift]);

  const allGiftAlertsHaveBeenDismissed = useMemo(() => {
    if (!gift.alerts) return true;
    return gift.alerts.every((a) => {
      return !!a?.acknowledgedAt;
    });
  }, [gift.alerts]);

  const brickColor = gift.brickColor ? BRICK_COLOR_CATALOG[gift.brickColor] : undefined;

  const delivery = getReadableDeliveryMethodType(gift) as string;

  return (
    <div
      className={clsx(!overlay && gift.group && styles.group, !overlay && isGroupFirst && styles.groupFirst, !overlay && isGroupLast && styles.groupLast, !overlay && isFiltered && styles.groupFiltered)}
      data-group={`${totalColumnGroupItems} / ${totalGroupItems}`}
      data-filtered-amount={groupFilteredAmount}
      ref={measureElement}
      {...(rowIndexAttributeName ? { [rowIndexAttributeName]: rowIndexAttribute } : {})}>
      <Card
        className={clsx(
          styles.card,
          (!allGiftAlertsHaveBeenDismissed || missingAddress || !gift.recipient || gift.recipientData) && styles.alert,
          employeeIsDeleted && styles.deletedEmployee,
          gift.company?.deliveryMethod !== 'home' && styles.office
        )}
        readOnly={hoverDisabled}
        variant={isSelected ? 'elevated' : 'outlined'}>
        <span className={styles.companyName}>{gift.company?.name}</span>
        <div className={styles.icons}>
          {gift.notes && (
            <Tooltip message={gift.notes}>
              <Icon icon='comment' />
            </Tooltip>
          )}
          {gift.type === TGiftTypes.WorkAnniversary && gift.company?.specialNote && (
            <Tooltip message={gift.company.specialNote}>
              <Icon icon='notes' className={styles.noteIconBorder} />
            </Tooltip>
          )}
          {gift.type === TGiftTypes.NewHire && gift.company?.swag && (
            <Tooltip message={gift.company.swag}>
              <Icon icon='notes' className={styles.noteIconBorder} />
            </Tooltip>
          )}
          {gift.recreatedFromId && (
            <Tooltip message='This gift was recreated from a shipment.'>
              <Icon icon='dynamic_feed' />
            </Tooltip>
          )}
          {gift.type === TGiftTypes.WorkAnniversary && gift.company?.workAnniversaryGiftVariation && gift.company.workAnniversaryGiftVariation !== TWorkAnniversaryGiftVariations.Deluxe && (
            <Tooltip message={`Work Anniversary Variation: ${getReadableWorkAnniversaryVariation(gift.company.workAnniversaryGiftVariation)}`}>{getWorkAnniversaryIcon(gift.company.workAnniversaryGiftVariation)}</Tooltip>
          )}
          {isDonation && (
            <Tooltip message='Donation enabled'>
              <Icon icon='volunteer_activism' />
            </Tooltip>
          )}
          {missingAddress && (
            <Tooltip message='Missing address data'>
              <Icon icon='not_listed_location' />
            </Tooltip>
          )}
          {employeeIsDeleted && (
            <Tooltip message='Employee Deleted'>
              <Icon icon='person_off' />
            </Tooltip>
          )}
          <AlertsTooltip alerts={gift.alerts as TAlert[]} />
        </div>
        <span className={styles.recipientName}> {gift.recipient?.fullName}</span>
        {!gift.recipient && <span className={styles.recipientName}>{getRemovedInfoFromRecipientData(gift)}</span>}
        {gift.recipient && isDependant(gift.recipient) && (
          <span className={styles.relationship}>
            {/* <Icon className={styles.relationshipIcon} icon='family_restroom' filled={true} weight={100} /> */}
            {getReadableRelationshipType(gift.recipient)} of {gift.recipient.employee?.fullName}
          </span>
        )}
        <div className={styles.tags}>
          <Tooltip autoPlacement={false} disabled={isDragging} message='Shipping Date' placement='bottom'>
            <Chip
              innerContainerClassName={styles.chip}
              leadingIcon='local_shipping'
              leadingIconProps={{
                filled: true
              }}
              label={readableDate(gift.shippingDate, 'dd/MM/yyyy')}
              readonly={true}
              variant='input'
            />
          </Tooltip>
          <Tooltip autoPlacement={false} disabled={isDragging} message='Event Date' placement='bottom'>
            <Chip
              innerContainerClassName={styles.chip}
              leadingIcon='event'
              leadingIconProps={{
                filled: true
              }}
              label={readableDate(gift.eventDate, 'dd/MM/yyyy')}
              readonly={true}
              variant='input'
            />
          </Tooltip>
          {gift.brickQuantity != null && !gift.brickColor && (
            <Tooltip autoPlacement={false} disabled={isDragging} message='Brick Quantity' placement='bottom'>
              <Chip innerContainerClassName={styles.chip} leadingIcon='inventory_2' label={String(gift.brickQuantity)} readonly variant='input' />
            </Tooltip>
          )}
          {gift.brickSize && (
            <Tooltip autoPlacement={false} disabled={isDragging} message='Brick Size' placement='bottom'>
              <Chip innerContainerClassName={styles.chip} leadingIcon='straighten' label={gift.brickSize} readonly variant='input' />
            </Tooltip>
          )}
          {gift.brickColor && (
            <Tooltip autoPlacement={false} disabled={isDragging} message='Brick Quantity and Color' placement='bottom'>
              <span className={styles.brickColorChip}>
                <Chip innerContainerClassName={clsx(styles.chip, styles.chipWithSwatch)} label={String(gift.brickQuantity ?? 1)} readonly variant='input' />
                <span aria-hidden='true' className={styles.brickColorSwatch} style={{ backgroundColor: brickColor?.background || gift.brickColor }} />
              </span>
            </Tooltip>
          )}
          {delivery && (
            <Tooltip autoPlacement={false} disabled={isDragging} message='Delivery Method' placement='bottom'>
              <Chip
                innerContainerClassName={styles.chip}
                leadingIcon={delivery.toLowerCase() === TDeliveryMethod.Home ? 'home' : 'business'}
                leadingIconProps={{
                  filled: true
                }}
                label={delivery}
                readonly={true}
                variant='input'
              />
            </Tooltip>
          )}
          {gift.type && gift.type != TGiftTypes.NewHire && (
            <Tooltip autoPlacement={false} disabled={isDragging} message={gift.type === TGiftTypes.CustomBricks ? 'Custom Brick Description' : gift.type === TGiftTypes.Other ? 'Custom Gift Type' : 'Gift Type'} placement='bottom'>
              <Chip
                innerContainerClassName={styles.chip}
                leadingIcon={'card_giftcard'}
                leadingIconProps={{
                  filled: true
                }}
                label={((gift.type === TGiftTypes.Other || gift.type === TGiftTypes.CustomBricks) && gift.customType ? gift.customType : getReadableGiftType(gift)) as string}
                readonly={true}
                variant='input'
              />
            </Tooltip>
          )}
          {!isDependant(gift.recipient) && gift.recipient?.tShirtSize && (
            <Tooltip autoPlacement={false} disabled={isDragging} message={`T-shirt size: ${gift.recipient.tShirtSize}`} placement='bottom'>
              <Chip
                innerContainerClassName={styles.chip}
                leadingIcon={'apparel'}
                leadingIconProps={{
                  filled: true
                }}
                label={gift.recipient.tShirtSize.length > 7 ? `${gift.recipient.tShirtSize.slice(0, 7)}...` : gift.recipient.tShirtSize}
                readonly={true}
                variant='input'
              />
            </Tooltip>
          )}
          {!isDependant(gift.recipient) && gift.recipient?.sweaterSize && (
            <Tooltip autoPlacement={false} disabled={isDragging} message={`Sweater size: ${gift.recipient.sweaterSize}`} placement='bottom'>
              <Chip
                innerContainerClassName={styles.chip}
                leadingIcon={'laundry'}
                leadingIconProps={{
                  filled: true
                }}
                label={gift.recipient.sweaterSize.length > 7 ? `${gift.recipient.sweaterSize.slice(0, 7)}...` : gift.recipient.sweaterSize}
                readonly={true}
                variant='input'
              />
            </Tooltip>
          )}
        </div>
      </Card>
    </div>
  );
};
